import type { GetStaticPaths, GetStaticProps } from 'next'
import type { AllTopicSlugsQuery } from '../../../.graphql/generated-types'
import {
  AllTopicSlugsDocument,
  ArticlesForTopicDocument,
  CountArticlesByTopicDocument,
} from '../../../.graphql/generated-types'
import {
  notEmpty,
  siteLocale,
  calculatePageCount,
  extractPageNumberFromParam,
  pageSize,
} from '@marketing-sites/utils'
import { graphQLClient } from '../../lib/api'
import type { Substitutions } from '../../components/ContentfulRichText'
import { fetchGlobalRegionalEditsAsSubstitutions } from '../GlobalRegionalEdits'
import type { TopicPageProps } from '../TopicPage'
import { TopicPage } from '../TopicPage'
import { ARTICLES_PAGES_REVALIDATE_SECONDS } from '../articlesRevalidate'

export const TopicPaginatedResultPage = TopicPage

async function fetchAllTopicSlugs(
  prevResults: AllTopicSlugsQuery[],
): Promise<{ slug: string }[]> {
  const prevResult = prevResults[prevResults.length - 1]
  if (prevResult?.composeComponentTopicCollection) {
    const { total, limit, skip } = prevResult.composeComponentTopicCollection
    const nextSkip = skip + limit
    const canFetchMoreItems = nextSkip < total
    if (canFetchMoreItems) {
      const nextPage = await graphQLClient
        .query(AllTopicSlugsDocument, { skip: nextSkip })
        .toPromise()

      if (nextPage.data) {
        const result = await fetchAllTopicSlugs([...prevResults, nextPage.data])
        return result
      }
    }
  } else {
    const firstPage = await graphQLClient
      .query(AllTopicSlugsDocument, { skip: 0 })
      .toPromise()
    const results = firstPage.data
    if (results) {
      const result = await fetchAllTopicSlugs([results])
      return result
    }
    return []
  }

  return prevResults.flatMap((queryResult) => {
    const topics = queryResult.composeComponentTopicCollection?.items
    if (topics) {
      return topics
        .map((topic) => {
          if (topic?.slug) {
            return { slug: topic.slug }
          }
          return null
        })
        .filter(notEmpty)
    }
    return []
  })
}

export const getStaticPathsForTopicPaginatedResultPage: GetStaticPaths =
  async () => {
    const topicSlugs = (await fetchAllTopicSlugs([])).map(
      (params) => params.slug,
    )

    const paths = []

    for await (const path of topicSlugsWithPaginatedResultPages()) {
      paths.push(path)
    }

    return {
      paths,
      fallback: false,
    }

    async function* topicSlugsWithPaginatedResultPages() {
      for (const slug of topicSlugs) {
        const firstPage = await graphQLClient
          .query(CountArticlesByTopicDocument, {
            preview: false,
            locale: siteLocale,
            slug,
          })
          .toPromise()
        const itemCount =
          firstPage.data?.composePageArticleCollection?.total ?? 1
        const pageCount = calculatePageCount(itemCount, pageSize)

        for (const pageNumber of Array.from(
          { length: pageCount },
          (_, pageIndex) => pageIndex + 1,
        )) {
          yield {
            params: { slug, pageNo: `page-${pageNumber.toString()}` },
          }
        }
      }
    }
  }

export const getStaticPropsForTopicPaginatedResultPage: GetStaticProps<
  TopicPageProps
> = async ({ params, preview }) => {
  const slug = params?.['slug']
  const pageNo = params?.['pageNo']
  let pageNumberParam = 1
  if (typeof pageNo === 'string') {
    const pageNumberFromParam = extractPageNumberFromParam(pageNo)
    if (typeof pageNumberFromParam === 'number') {
      pageNumberParam = pageNumberFromParam
    }
  }

  const skip = pageSize * (pageNumberParam - 1)
  const limit = pageSize

  if (typeof slug === 'string') {
    const result = await graphQLClient
      .query(ArticlesForTopicDocument, {
        limit,
        skip,
        preview: !!preview,
        locale: siteLocale,
        slug,
      })
      .toPromise()

    const substitutions: Substitutions =
      await fetchGlobalRegionalEditsAsSubstitutions()

    return {
      props: {
        data: result.data,
        substitutions,
        pageNumber: pageNumberParam,
      },
      revalidate: ARTICLES_PAGES_REVALIDATE_SECONDS,
    }
  }
  return {
    props: {
      substitutions: {},
      pageNumber: 1,
      params: {
        pageNo: `page-${pageNumberParam.toString()}`,
      },
    },
    revalidate: ARTICLES_PAGES_REVALIDATE_SECONDS,
  }
}
